/**
 * Smart packing-slip defaults: shipping-method paper + size heuristics +
 * customer+method history ("previously printed as").
 * Staff can always override in PackingPrePrintDialog.
 */

export const PACKING_SLIP_FORMAT = Object.freeze({
  STANDARD: 'standard',
  CHEETAH: 'cheetah',
  GO4: 'go4',
});

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isPackingSlipFormat(value) {
  return (
    value === PACKING_SLIP_FORMAT.STANDARD ||
    value === PACKING_SLIP_FORMAT.CHEETAH ||
    value === PACKING_SLIP_FORMAT.GO4
  );
}

/**
 * @param {unknown} value
 * @param {'standard'|'cheetah'|'go4'} [fallback]
 * @returns {'standard'|'cheetah'|'go4'}
 */
export function coercePackingSlipFormat(value, fallback = PACKING_SLIP_FORMAT.STANDARD) {
  if (isPackingSlipFormat(value)) return value;
  return isPackingSlipFormat(fallback) ? fallback : PACKING_SLIP_FORMAT.STANDARD;
}

/**
 * Queue item kinds emitted for a Prepare-print format (slip then GO4 outside).
 * @param {unknown} format
 * @returns {string[]}
 */
export function packingSlipQueueKindsForFormat(format) {
  const resolved = coercePackingSlipFormat(format);
  if (resolved === PACKING_SLIP_FORMAT.CHEETAH) return ['cheetah-label'];
  if (resolved === PACKING_SLIP_FORMAT.GO4) {
    return ['packing-slip', 'go4-outside-label'];
  }
  return ['packing-slip'];
}

/**
 * QZ profile keys for a packing format (Brother A4 and/or GoDEX outside).
 * @param {unknown} format
 * @returns {string[]}
 */
export function profileKeysForPackingFormat(format) {
  const resolved = coercePackingSlipFormat(format);
  if (resolved === PACKING_SLIP_FORMAT.CHEETAH) return ['cheetah'];
  if (resolved === PACKING_SLIP_FORMAT.GO4) return ['standard', 'go4Outside'];
  return ['standard'];
}

/** Same identity as PackingPrePrintDialog / packing-slip print counts. */
export function getPackingSlipOrderKey(order) {
  return String(
    order?.source_id ||
      order?.sourceId ||
      order?.orderNumber ||
      order?.order_number ||
      order?.orderId ||
      order?.id ||
      '',
  )
    .trim()
    .replace(/^#/, '');
}

/** Fallback when no history for this customer + method. */
export const PACKING_SLIP_COPY_HEURISTIC = Object.freeze({
  qtyTotal: 20,
  weightKg: 8,
  productLines: 12,
});

const PRODUCT_LINE_SKIP_RE = /^(del|shipping|courier|delivery)$/i;

/**
 * @param {object} order
 * @returns {string}
 */
export function getOrderShippingMethodLabel(order) {
  return String(
    order?.shipping_method ||
      order?.shippingMethod ||
      order?.shipping?.method ||
      order?.Method ||
      '',
  ).trim();
}

/**
 * Markets / venue stock: "market" in method, source id, companies, or stock reorder cues.
 * @param {object} order
 */
export function isMarketStockOrder(order) {
  const checkout = order?.checkout_data || order?.checkoutData || {};
  const blob = [
    getOrderShippingMethodLabel(order),
    order?.source_id,
    order?.sourceId,
    order?.orderNumber,
    order?.order_number,
    order?.shipping?.company,
    order?.customer?.company,
    order?.companyName,
    checkout.OrderType,
    checkout.orderType,
    checkout.VenueName,
    checkout.venueName,
  ]
    .map((value) => String(value || ''))
    .join(' ')
    .toLowerCase();
  if (/\bmarkets?\b/.test(blob) || /market\s*stock/.test(blob)) return true;
  if (/venue-stock|stock-reorder/.test(blob)) return true;
  return false;
}

/**
 * @param {string} method
 */
export function normalizeShippingMethodKey(method) {
  return String(method || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '') || 'unknown';
}

/**
 * Customer identity for preference bank (same person + same method).
 * @param {object} order
 */
export function getPackingSlipPreferenceCustomerKey(order) {
  const raw =
    order?.customer_id ||
    order?.customerId ||
    order?.CustomerID ||
    order?.email ||
    order?.shipping?.company ||
    order?.customer?.company ||
    order?.companyName ||
    '';
  const key = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  return key || 'unknown';
}

/**
 * @param {object} order
 */
export function countPackingSlipProductLines(order) {
  const items = Array.isArray(order?.order_items)
    ? order.order_items
    : Array.isArray(order?.items)
      ? order.items
      : [];
  return items.filter((item) => {
    const code = String(item?.code || item?.sku || item?.product_code || '').trim();
    const name = String(item?.name || item?.product_name || '').trim();
    if (PRODUCT_LINE_SKIP_RE.test(code) || PRODUCT_LINE_SKIP_RE.test(name)) return false;
    return true;
  }).length;
}

/**
 * @param {object} order
 */
export function getOrderQuantityTotal(order) {
  const direct = Number(order?.quantity_total ?? order?.quantityTotal);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const items = Array.isArray(order?.order_items)
    ? order.order_items
    : Array.isArray(order?.items)
      ? order.items
      : [];
  return items.reduce((sum, item) => {
    const qty = Number(item?.quantity ?? item?.qty ?? 0);
    return sum + (Number.isFinite(qty) && qty > 0 ? qty : 0);
  }, 0);
}

/**
 * @param {object} order
 * @returns {number|null}
 */
export function getOrderWeightTotalKg(order) {
  const direct = Number(order?.weight_total ?? order?.weightTotal);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const items = Array.isArray(order?.order_items)
    ? order.order_items
    : Array.isArray(order?.items)
      ? order.items
      : [];
  let sum = 0;
  let any = false;
  items.forEach((item) => {
    const weight = Number(item?.weight);
    const qty = Number(item?.quantity ?? item?.qty ?? 1);
    if (Number.isFinite(weight) && weight > 0 && Number.isFinite(qty) && qty > 0) {
      sum += weight * qty;
      any = true;
    }
  });
  return any ? sum : null;
}

/**
 * Method → paper (before history). Cheetah for cargo / Bermondsey collection.
 * Standard for courier Express/Tracked and Markets.
 * @param {object} order
 * @returns {'standard'|'cheetah'|'go4'}
 */
export function resolveMethodDefaultFormat(order) {
  if (isMarketStockOrder(order)) return PACKING_SLIP_FORMAT.STANDARD;
  const method = getOrderShippingMethodLabel(order).toLowerCase();
  if (/cargo\s*bike|bermondsey/.test(method)) return PACKING_SLIP_FORMAT.CHEETAH;
  if (/tracked\s*24|express\s*(am|24|pm)?|x24|xam/.test(method)) {
    return PACKING_SLIP_FORMAT.STANDARD;
  }
  if (/collect|collection|pickup|pick\s*up/.test(method) && /bermondsey|spa|market/.test(method)) {
    return PACKING_SLIP_FORMAT.CHEETAH;
  }
  return PACKING_SLIP_FORMAT.STANDARD;
}

/**
 * Size heuristic when no usable history sample.
 * @param {object} order
 */
export function resolveHeuristicCopies(order) {
  const qty = getOrderQuantityTotal(order);
  const weight = getOrderWeightTotalKg(order);
  const lines = countPackingSlipProductLines(order);
  if (
    qty >= PACKING_SLIP_COPY_HEURISTIC.qtyTotal ||
    (weight != null && weight >= PACKING_SLIP_COPY_HEURISTIC.weightKg) ||
    lines >= PACKING_SLIP_COPY_HEURISTIC.productLines
  ) {
    return 2;
  }
  return 1;
}

/**
 * Pick copies from history samples closest in size to this order.
 * @param {object} order
 * @param {{ samples?: object[], lastCopies?: number }|null} preference
 */
export function resolveCopiesFromPreference(order, preference) {
  const samples = Array.isArray(preference?.samples) ? preference.samples : [];
  const qty = getOrderQuantityTotal(order);
  const weight = getOrderWeightTotalKg(order);

  const scored = samples
    .map((sample) => {
      const sampleQty = Number(sample?.qtyTotal) || 0;
      const sampleWeight = Number(sample?.weightTotal);
      const qtyDelta =
        qty > 0 && sampleQty > 0 ? Math.abs(sampleQty - qty) / Math.max(qty, sampleQty) : 1;
      let weightDelta = 1;
      if (weight != null && Number.isFinite(sampleWeight) && sampleWeight > 0) {
        weightDelta = Math.abs(sampleWeight - weight) / Math.max(weight, sampleWeight);
      }
      const distance = weight != null && Number.isFinite(sampleWeight) && sampleWeight > 0
        ? (qtyDelta + weightDelta) / 2
        : qtyDelta;
      return {
        copies: Math.max(1, Math.min(99, parseInt(sample?.copies, 10) || 1)),
        distance,
      };
    })
    .filter((entry) => entry.distance <= 0.35)
    .sort((left, right) => left.distance - right.distance);

  if (scored.length) {
    const top = scored.slice(0, 5);
    const counts = new Map();
    top.forEach((entry) => {
      counts.set(entry.copies, (counts.get(entry.copies) || 0) + 1);
    });
    let bestCopies = top[0].copies;
    let bestN = 0;
    counts.forEach((n, copies) => {
      if (n > bestN) {
        bestN = n;
        bestCopies = copies;
      }
    });
    return bestCopies;
  }

  const last = parseInt(preference?.lastCopies, 10);
  if (Number.isFinite(last) && last >= 1) return Math.min(99, last);
  return resolveHeuristicCopies(order);
}

/**
 * @param {object} order
 * @param {{ lastFormat?: string, samples?: object[], lastCopies?: number }|null} preference
 */
export function resolveOrderPackingSlipDefaults(order, preference = null) {
  const methodFormat = resolveMethodDefaultFormat(order);
  const historyFormat = isPackingSlipFormat(preference?.lastFormat)
    ? preference.lastFormat
    : null;
  return {
    orderKey: getPackingSlipOrderKey(order),
    customerKey: getPackingSlipPreferenceCustomerKey(order),
    methodKey: normalizeShippingMethodKey(getOrderShippingMethodLabel(order)),
    methodLabel: getOrderShippingMethodLabel(order),
    format: historyFormat || methodFormat,
    formatSource: historyFormat ? 'history' : 'method',
    copies: resolveCopiesFromPreference(order, preference),
    copiesSource: preference?.samples?.length || preference?.lastCopies ? 'history' : 'heuristic',
    isMarket: isMarketStockOrder(order),
    qtyTotal: getOrderQuantityTotal(order),
    weightTotal: getOrderWeightTotalKg(order),
    productLines: countPackingSlipProductLines(order),
  };
}

/**
 * @param {string} customerKey
 * @param {string} methodKey
 */
export function buildPackingSlipPreferenceDocId(customerKey, methodKey) {
  const customer = String(customerKey || 'unknown')
    .trim()
    .toLowerCase()
    .replace(/[/\s#.?[\]]+/g, '_')
    .replace(/^_+|_+$/g, '');
  const method = String(methodKey || 'unknown')
    .trim()
    .toLowerCase()
    .replace(/[/\s#.?[\]]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return `${customer || 'unknown'}__${method || 'unknown'}`.slice(0, 700);
}

/**
 * @param {object} order
 */
export function preferenceDocIdForOrder(order) {
  return buildPackingSlipPreferenceDocId(
    getPackingSlipPreferenceCustomerKey(order),
    normalizeShippingMethodKey(getOrderShippingMethodLabel(order)),
  );
}

/**
 * One format for a multi-order Prepare-print job (majority vote).
 * @param {object[]} orders
 * @param {Map<string, object>|Record<string, object>} preferencesByDocId
 * @param {(order: object) => string} [preferenceDocIdForOrderFn]
 */
export function resolveBatchPackingSlipDefaults(
  orders,
  preferencesByDocId,
  preferenceDocIdForOrderFn = preferenceDocIdForOrder,
) {
  const list = Array.isArray(orders) ? orders.filter(Boolean) : [];
  const perOrder = list.map((order) => {
    const docId =
      typeof preferenceDocIdForOrderFn === 'function'
        ? preferenceDocIdForOrderFn(order)
        : preferenceDocIdForOrder(order);
    const preference =
      (preferencesByDocId instanceof Map
        ? preferencesByDocId.get(docId)
        : preferencesByDocId?.[docId]) || null;
    return resolveOrderPackingSlipDefaults(order, preference);
  });

  const votes = {
    [PACKING_SLIP_FORMAT.STANDARD]: 0,
    [PACKING_SLIP_FORMAT.CHEETAH]: 0,
    [PACKING_SLIP_FORMAT.GO4]: 0,
  };
  perOrder.forEach((entry) => {
    const format = coercePackingSlipFormat(entry.format);
    votes[format] += 1;
  });
  // Strict majority; ties keep Standard (same spirit as prior Cheetah-vs-Standard vote).
  let format = PACKING_SLIP_FORMAT.STANDARD;
  let bestVotes = votes[PACKING_SLIP_FORMAT.STANDARD];
  [PACKING_SLIP_FORMAT.CHEETAH, PACKING_SLIP_FORMAT.GO4].forEach((candidate) => {
    if (votes[candidate] > bestVotes) {
      bestVotes = votes[candidate];
      format = candidate;
    }
  });
  const nonzeroFormats = Object.values(votes).filter((n) => n > 0).length;

  const copiesByOrder = {};
  perOrder.forEach((entry) => {
    if (entry.orderKey) copiesByOrder[entry.orderKey] = entry.copies;
  });

  return {
    format,
    formatSource: nonzeroFormats <= 1 ? 'unanimous' : 'majority',
    copiesByOrder,
    perOrder,
    votes,
  };
}
